const HKT_TIME_ZONE = 'Asia/Hong_Kong';
const DAY_MS = 24 * 60 * 60 * 1000;

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface DateRangeValidation {
  valid: boolean;
  error?: string;
}

const formatters = {
  iso: new Intl.DateTimeFormat('en-CA', {
    timeZone: HKT_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }),
  short: new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' }),
  shortWithYear: new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }),
};

function dateFromIso(date: string): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

function toIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function hktToday(now = new Date()): string {
  const todayParts = formatters.iso.formatToParts(now);
  const values = Object.fromEntries(todayParts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function addDays(date: string, days: number): string {
  const value = dateFromIso(date);
  value.setUTCDate(value.getUTCDate() + days);
  return toIso(value);
}

export function recentDays(days: number, now = new Date()): DateRange {
  const endDate = hktToday(now);
  return { endDate, startDate: addDays(endDate, -(days - 1)) };
}

export function rangeDays({ startDate, endDate }: DateRange): number {
  return Math.round((dateFromIso(endDate).getTime() - dateFromIso(startDate).getTime()) / DAY_MS) + 1;
}

export function previousRange(range: DateRange): DateRange {
  const days = rangeDays(range);
  return { endDate: addDays(range.startDate, -1), startDate: addDays(range.startDate, -days) };
}

export function validateRange(range: DateRange, today = hktToday()): DateRangeValidation {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(range.startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(range.endDate)) {
    return { valid: false, error: 'Choose both a start and end date.' };
  }
  const start = dateFromIso(range.startDate);
  const end = dateFromIso(range.endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || toIso(start) !== range.startDate || toIso(end) !== range.endDate) {
    return { valid: false, error: 'Choose valid calendar dates.' };
  }
  if (range.endDate < range.startDate) return { valid: false, error: 'End date must be on or after start date.' };
  if (range.endDate > today) return { valid: false, error: `Choose HKT dates through today (${today}).` };
  if (rangeDays(range) > 366) return { valid: false, error: 'Choose at most 366 days.' };
  return { valid: true };
}

export function formatRange(range: DateRange, includeYear = true): string {
  const formatter = includeYear ? formatters.shortWithYear : formatters.short;
  return `${formatter.format(dateFromIso(range.startDate))} – ${formatter.format(dateFromIso(range.endDate))}`;
}

/** Match the applied date range, never an unsubmitted date draft. */
export function periodPreset(range: DateRange, today = hktToday()): number | undefined {
  return range.endDate === today ? [1, 7, 30, 90].find(days => rangeDays(range) === days) : undefined;
}

export function formatPeriod(range: DateRange, today = hktToday()): string {
  if (!validateRange(range, today).valid) return 'Invalid date range';
  const preset = periodPreset(range, today);
  return preset === 1 ? 'Today' : preset ? `${preset} days` : `${formatRange(range)} (HKT)`;
}

export function formatDate(date: string, includeYear = false): string {
  return (includeYear ? formatters.shortWithYear : formatters.short).format(dateFromIso(date));
}

export function formatMoment(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: HKT_TIME_ZONE,
  }).format(date);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

export function formatRatio(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export type Change =
  | { kind: 'change'; value: number; label: string; positive: boolean }
  | { kind: 'new'; label: 'New'; positive: true }
  | { kind: 'none'; label: 'No change'; positive: boolean }
  | { kind: 'unavailable'; label: 'Unavailable'; positive: boolean };

export function metricChange(current: number | null, previous: number | null, percentagePoints = false): Change {
  if (current === null || previous === null) return { kind: 'unavailable', label: 'Unavailable', positive: false };
  if (percentagePoints) {
    const value = (current - previous) * 100;
    if (value === 0) return { kind: 'none', label: 'No change', positive: true };
    return { kind: 'change', value, label: `${value > 0 ? '+' : ''}${value.toFixed(1)} pp`, positive: value > 0 };
  }
  if (previous === 0) {
    if (current === 0) return { kind: 'none', label: 'No change', positive: true };
    return { kind: 'new', label: 'New', positive: true };
  }
  const value = ((current - previous) / previous) * 100;
  if (value === 0) return { kind: 'none', label: 'No change', positive: true };
  return { kind: 'change', value, label: `${value > 0 ? '+' : ''}${value.toFixed(1)}%`, positive: value > 0 };
}
